import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import axios from 'axios';
import { API } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('TOUT');
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = ['TOUT', 'COMBATS', 'VÉHICULES', 'PYROTECHNIE', 'CÂBLAGE'];

  const fetchGallery = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setItems(response.data || []);
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const getMediaUrl = (url) => {
    if (url && url.startsWith('/api/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };

  // Fonction pour extraire l'ID YouTube et créer l'URL embed
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // Patterns pour extraire l'ID YouTube (inclut Shorts)
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtu\.be\/)([^?\s]+)/,
      /(?:youtube\.com\/embed\/)([^?\s]+)/,
      /(?:youtube\.com\/v\/)([^?\s]+)/,
      /(?:youtube\.com\/shorts\/)([^?\s]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }
    return null;
  };

  // Fonction pour obtenir la thumbnail YouTube
  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtu\.be\/)([^?\s]+)/,
      /(?:youtube\.com\/embed\/)([^?\s]+)/,
      /(?:youtube\.com\/shorts\/)([^?\s]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
      }
    }
    return null;
  };

  // Vérifie si c'est une URL YouTube
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Vérifie si c'est un YouTube Short
  const isYouTubeShort = (url) => {
    if (!url) return false;
    return url.includes('/shorts/');
  };

  const filteredItems = filter === 'TOUT' 
    ? items 
    : items.filter(item => item.category === filter);

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Header */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
              Portfolio
            </p>
            <h1 
              className="font-['Teko'] text-6xl md:text-8xl text-white mb-6"
              data-testid="gallery-title"
            >
              GALERIE
            </h1>
            <p className="font-['Barlow'] text-gray-400 text-lg max-w-2xl mx-auto">
              Découvrez nos réalisations à travers des photos et vidéos de nos cascades professionnelles.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-12">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Button
                key={cat}
                onClick={() => setFilter(cat)}
                variant={filter === cat ? 'default' : 'outline'}
                className={`font-['Barlow'] uppercase tracking-wider text-sm ${
                  filter === cat 
                    ? 'bg-[#FACC15] text-black hover:bg-[#EAB308]' 
                    : 'border-white/20 text-white hover:border-[#FACC15] hover:text-[#FACC15]'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : filteredItems.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative aspect-[9/16] bg-[#09090b] overflow-hidden cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  {item.type === 'video' && isYouTubeUrl(item.url) ? (
                    // Vidéo YouTube - afficher la thumbnail
                    <img
                      src={getYouTubeThumbnail(item.url)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : item.type === 'video' ? (
                    // Vidéo locale (ancien format)
                    <video
                      src={getMediaUrl(item.url)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      muted
                      playsInline
                      preload="metadata"
                      onMouseOver={(e) => e.target.play().catch(() => {})}
                      onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                  ) : (
                    // Photo
                    <img
                      src={getMediaUrl(item.url)}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  
                  {/* Title always visible at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-['Teko'] text-xl text-white text-center">
                      {item.title}
                    </h3>
                  </div>
                  
                  {/* Overlay content on hover */}
                  <div className="absolute inset-0 flex flex-col justify-center items-center p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60">
                    <span className="font-['Barlow'] text-[#FACC15] text-xs uppercase tracking-wider mb-2">
                      {item.category}
                    </span>
                    <h3 className="font-['Teko'] text-2xl text-white text-center">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="font-['Barlow'] text-gray-300 text-sm mt-2 text-center">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Type indicator */}
                  <div className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm flex items-center justify-center">
                    {item.type === 'video' ? (
                      <Play size={20} className="text-[#FACC15]" />
                    ) : (
                      <ImageIcon size={20} className="text-white/70" />
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="font-['Barlow'] text-gray-400">
                Aucun élément dans cette catégorie.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-5xl bg-black border border-white/10 p-0">
          {selectedItem && (
            <div className="relative">
              <button
                onClick={() => setSelectedItem(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:text-[#FACC15] transition-colors"
                data-testid="lightbox-close"
              >
                <X size={24} />
              </button>
              {selectedItem.type === 'video' && isYouTubeUrl(selectedItem.url) ? (
                // Lecteur YouTube intégré
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedItem.url)}?autoplay=1`}
                  className="w-full aspect-video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedItem.title}
                />
              ) : selectedItem.type === 'video' ? (
                // Vidéo locale (ancien format)
                <video
                  src={getMediaUrl(selectedItem.url)}
                  controls
                  className="w-full aspect-video"
                  autoPlay
                />
              ) : (
                // Photo
                <img
                  src={getMediaUrl(selectedItem.url)}
                  alt={selectedItem.title}
                  className="w-full"
                />
              )}
              <div className="p-6">
                <span className="font-['Barlow'] text-[#FACC15] text-xs uppercase tracking-wider">
                  {selectedItem.category}
                </span>
                <h3 className="font-['Teko'] text-3xl text-white mt-1">
                  {selectedItem.title}
                </h3>
                {selectedItem.description && (
                  <p className="font-['Barlow'] text-gray-400 mt-2">
                    {selectedItem.description}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
